import { useState, useEffect } from "react";
import { useStoreActions, useStoreState } from "easy-peasy";
import {
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  styled
} from "@mui/material";
import TeleforceApi from "../../apis/teleforce";
import * as Sentry from "@sentry/browser";
import Logo from "../../images/logo.svg";
import TeleforceLogo from "../../images/logo-teleforce.svg?react";
import ThemeToggle from "../Common/ThemeToggle";

function useFormInput(initialValue) {
  const [value, setValue] = useState(initialValue);
  function handleChange(e) {
    setValue(e.target.value);
  }
  return {
    value,
    setValue,
    onChange: handleChange
  };
}

export default function LoginForm() {
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const notify = useStoreActions(actions => actions.notifier.notify);
  const supervisorStation = useStoreState(state => state.supervisor.station);
  const supervisorVersion = useStoreState(state => state.supervisor.version);
  const supervisorBuild = useStoreState(state => state.supervisor.build);
  const supervisorId = useFormInput("");
  const supervisorPassword = useFormInput("");
  const authenticate = useStoreActions(actions => actions.auth.authenticate);
  const setSupervisor = useStoreActions(actions => actions.supervisor.set);
  const setPermissions = useStoreActions(actions => actions.permissions.set);
  // const setFilters = useStoreActions(actions => actions.filters.setFilters);
  const setDepartmentData = useStoreActions(
    actions => actions.departments.setData
  );
  const setSelectedDepartment = useStoreActions(
    actions => actions.departments.setSelected
  );

  const handleSubmit = event => {
    event.preventDefault();
    setDisabled(true);
    setLoading(true);
    login();
  };

  const login = () => {
    // prioritize local storage credentials
    const id = supervisorId.value;
    const password = supervisorPassword.value;
    // send api request to login supervisor
    TeleforceApi.supervisorLogin(id, password, supervisorStation)
      .then(results => {
        if (results.data) {
          // sentry config
          if (window.SENTRY_DSN) {
            {
              const scope = Sentry.getCurrentScope();
              scope.setLevel("warning");
              scope.setUser({ id: id });
              scope.setTag("station", supervisorStation);
              scope.setTag("version", supervisorVersion);
              scope.setTag("build", supervisorBuild);
            }
          }

          // set api token
          window.TELEFORCE_API_TOKEN =
            results.data.supervisor.teleforce_api_token;
          window.TELEFORCE_USER_ID = results.data.supervisor.id;

          // Variable to hold the index if there is a dept match to user department
          let usersDepartment = null;

          // Find a match for department_id in departments result set
          // If it finds a match, get the index in the array.
          if (results.data.departments) {
            results.data.departments.map((dept, index) => {
              if (dept.id === results.data.supervisor.department_id) {
                usersDepartment = index;
              }

              return usersDepartment;
            });
          }

          // If the map above finds a department match (contains the index of dept array)
          // set selected dept. to it, else med sales (0 index)
          usersDepartment
            ? setSelectedDepartment(results.data.departments[usersDepartment])
            : setSelectedDepartment(results.data.departments[0]);
          setDepartmentData(results.data.departments);

          // Removes NULL shift value
          let shifts = [];
          if (results.data.shifts) {
            results.data.shifts.map(item => (item ? shifts.push(item) : null));
          }

          // setFilters({ type: "teams", data: results.data.teams ?? [] });
          // setFilters({ type: "grades", data: results.data.grades ?? [] });
          // setFilters({ type: "locations", data: results.data.locations ?? [] });
          // setFilters({ type: "shifts", data: shifts });
          // setFilters({
          //   type: "schedules",
          //   data: results.data.schedules ?? ["N/A"]
          // });

          // auth
          setSupervisor(results.data.supervisor);
          setPermissions(results.data.permissions);
          authenticate(true);
        }
      })
      .catch(error => {
        console.error(error);
        resetForm();
        notify({
          message: "Unable to login. Please try again.",
          type: "error"
        });
      });
  };

  const resetForm = () => {
    supervisorId.setValue("");
    supervisorPassword.setValue("");
    setDisabled(false);
    setLoading(false);
  };

  // reset form when component unmounts
  useEffect(() => {
    return () => {
      resetForm();
      // notify({ message: "You are now logged in.", type: "success" });
    };
  }, []);

  return (
    <StyledMain>
      <div style={{ position: "relative" }}>
        <StyledPaper>
          <StyledLogo>
            <img src={Logo} alt="" />
          </StyledLogo>
          <StyledForm>
            <TextField
              value={supervisorId.value}
              onChange={supervisorId.onChange}
              id="supervisorId"
              name="supervisorId"
              type="number"
              label="ID"
              variant="filled"
              size="small"
              margin="dense"
              fullWidth
            />
            <TextField
              value={supervisorPassword.value}
              onChange={supervisorPassword.onChange}
              id="supervisorPassword"
              name="supervisorPassword"
              type="password"
              label="Password"
              variant="filled"
              size="small"
              margin="dense"
              fullWidth
            />
            <StyledButtonWrapper>
              <Button
                type="submit"
                disabled={
                  disabled ||
                  !(
                    supervisorId.value.length > 0 &&
                    supervisorPassword.value.length > 0
                  )
                }
                onClick={e => handleSubmit(e)}
                variant="contained"
                color="primary"
                size="large"
                fullWidth
              >
                Log In
              </Button>
              {loading && <StyledProgressIcon size={24} />}
            </StyledButtonWrapper>
          </StyledForm>
        </StyledPaper>
        <StyledFooterContainer>
          <StyledFooter variant="caption" align="center" display="block">
            {supervisorStation} &nbsp;&middot;&nbsp; Version {supervisorVersion}{" "}
            &nbsp;&middot;&nbsp; Build {supervisorBuild} &nbsp;&middot;&nbsp;
            &copy; Teleforce {new Date().getFullYear()}
          </StyledFooter>
          <StyledFooterLogo>
            <TeleforceLogo />
          </StyledFooterLogo>
        </StyledFooterContainer>
      </div>
      <ThemeToggle />
    </StyledMain>
  );
}

const StyledMain = styled("main")(({ theme }) => ({
  width: "auto",
  height: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: theme.spacing(5),
  marginRight: theme.spacing(5),

  [theme.breakpoints.up(448)]: {
    width: 450,
    marginLeft: "auto",
    marginRight: "auto"
  }
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: "var(--teleforce-900)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: `${theme.spacing(2)} ${theme.spacing(3)} ${theme.spacing(5)}`,
  borderRadius: 10,

  [theme.breakpoints.up(448)]: {
    padding: `${theme.spacing(4)} ${theme.spacing(6)} ${theme.spacing(7)}`
  }
}));

const StyledForm = styled("form")(({ theme }) => ({
  width: "100%",
  marginTop: theme.spacing(1)
}));

const StyledButtonWrapper = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(2),
  position: "relative"
}));

const StyledLogo = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(1),

  // The Supervisor logo is a white image; it can't be recolored via `fill`
  // (it's an <img>), so darken it in light mode with a filter.
  '[data-theme="light"] & img': {
    filter: "brightness(0)"
  }
}));

const StyledProgressIcon = styled(CircularProgress)({
  position: "absolute",
  top: "50%",
  left: "50%",
  marginTop: -12,
  marginLeft: -12
});

const StyledFooterContainer = styled("div")({
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0
});

const StyledFooter = styled(Typography)(({ theme }) => ({
  color: "var(--teleforce-700)",
  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
  cursor: "default",
  marginTop: theme.spacing(3)
}));

const StyledFooterLogo = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(1),
  color: "var(--teleforce-700)",

  // The wordmark mixes currentColor with a hardcoded white fill; unify it to
  // currentColor so the whole mark follows the theme (visible in light mode).
  '& [fill="#fff"]': {
    fill: "currentColor"
  }
}));
